/* @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import { breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import Faqs from 'bundles/design-system/components/Faqs';
import type { CertificateFaQsFragment } from 'bundles/premium-certificate/__generated__/contentfulTypes';
import { MAX_FAQS_TO_SHOW } from 'bundles/premium-certificate/constants';
import Container from 'bundles/xddp/components/shared/Container';

import _t from 'i18n!nls/premium-certificate';

type Props = CertificateFaQsFragment;

const styles: Record<string, Interpolation<Theme>> = {
  certificateFAQsWrapper: () => css`
    background-color: #dbeef6;
    padding-top: 4.5rem;
    padding-bottom: 4.5rem;

    @media (max-width: ${breakpoints.values.md}px) {
      padding: 3rem 0.75rem;
    }

    .rc-Faq {
      & > .question h3 {
        font-size: 1.25rem;
        line-height: 1.5rem;

        @media (max-width: ${breakpoints.values.md}px) {
          font-size: 0.875rem;
        }
      }

      .answer.visible > * {
        font-size: 1.25rem;
        line-height: 1.5rem;

        @media (max-width: ${breakpoints.values.md}px) {
          font-size: 0.875rem;
        }
      }
    }
  `,
  faqtitle: () => css`
    font-family: inherit;
    color: var(--cds-color-grey-975);
    font-size: 1.5rem;
    line-height: 1.875rem;
    font-weight: 700;
    margin-bottom: 3rem;
    text-align: center;

    @media (max-width: ${breakpoints.values.md}px) {
      text-align: left;
      margin-bottom: 1.875rem;
    }
  `,
  faqContactInfo: css`
    text-align: center;
    padding-top: 1.5rem;

    .rc-Markdown.styled p {
      font-size: 1rem;
      line-height: 1.875rem;
      margin-bottom: 0;
    }
  `,
  faqsContainer: css`
    max-width: 784px;
  `,
};

const CertificateFAQs = ({ faqsCollection, faqContactInfo }: Props) => {
  if (!faqsCollection?.items || !faqsCollection?.items.length) {
    return null;
  }

  return (
    <div id="faq" data-test="CertificateFAQs" css={styles.certificateFAQsWrapper}>
      <Container css={styles.faqsContainer}>
        <h2 css={styles.faqtitle}>{_t('Frequently asked questions')}</h2>
        <Faqs
          faqs={faqsCollection.items}
          enableShowMoreList
          showMoreListProps={{
            numItemsToShow: MAX_FAQS_TO_SHOW,
            controllerProps: { itemTypePlural: _t('frequently asked questions') },
          }}
        />
        {faqContactInfo && (
          <div css={styles.faqContactInfo} data-test="faq-contact-info">
            <Markdown source={faqContactInfo} />
          </div>
        )}
      </Container>
    </div>
  );
};

export default CertificateFAQs;
